package com.sddamico.draftmachine.pages.draft

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmEffects
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.Icon
import com.sddamico.draftmachine.ds.IconWeight
import com.sddamico.draftmachine.ds.displayBlock
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.dmStamp
import com.sddamico.draftmachine.ds.feedback.Toast
import com.sddamico.draftmachine.ds.feedback.ToastTone
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.overlays.PanelDialog
import com.sddamico.draftmachine.ds.roleH2
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input as DomInput
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.events.Event
import org.w3c.files.File
import org.w3c.files.FileReader
import org.w3c.files.get

// ── Pure CSV parsing + validation ────────────────────────────────────────────

/** One validated team parsed from a CSV row, ready to become a `WizardTeam`. */
data class ParsedTeamRow(val teamName: String, val ownerName: String, val ownerEmail: String)

/** Result of [parseTeamsCsv]. An empty [errors] list means the whole file is valid (all-or-nothing). */
data class CsvParseResult(val rows: List<ParsedTeamRow>, val errors: List<String>)

private val EMAIL_REGEX = Regex("^[^@\\s]+@[^@\\s]+\\.[^@\\s]+$")
private val EXPECTED_HEADER = listOf("team name", "owner name", "owner email")

/**
 * Parse a teams CSV: requires a header row (`Team Name, Owner Name, Owner Email`), then one team per
 * row. Validation is all-or-nothing — if any row is bad, [CsvParseResult.errors] lists every problem
 * (1-based row numbers, counting the header as row 1) and callers should import nothing.
 *
 * Each record is a single physical line. Quoted fields may embed commas (`"Sharks, Inc."`), but a
 * quoted value that spans multiple lines is **not** supported — an unterminated quote is reported as
 * an error rather than parsed as multi-line data (see [splitCsvLine]).
 *
 * @param existingEmails lowercased owner emails already on the roster, for cross-file duplicate detection.
 */
fun parseTeamsCsv(text: String, existingEmails: Set<String>): CsvParseResult {
  // Keep original 1-based line numbers so error messages point at the user's actual file rows.
  val physicalLines = text.split("\r\n", "\r", "\n")
  val indexedNonBlank = physicalLines
    .mapIndexed { i, line -> (i + 1) to line }
    .filter { (_, line) -> line.isNotBlank() }

  if (indexedNonBlank.isEmpty()) {
    return CsvParseResult(emptyList(), listOf("The file is empty."))
  }

  val headerLine = indexedNonBlank.first().second
  val header = splitCsvLine(headerLine)?.map { it.trim().lowercase() }
  if (header != EXPECTED_HEADER) {
    return CsvParseResult(
      emptyList(),
      listOf("First row must be a header: Team Name, Owner Name, Owner Email"),
    )
  }

  val dataLines = indexedNonBlank.drop(1)
  if (dataLines.isEmpty()) {
    return CsvParseResult(emptyList(), listOf("No team rows found below the header row."))
  }

  val errors = mutableListOf<String>()
  val rows = mutableListOf<ParsedTeamRow>()
  val seenEmails = existingEmails.toMutableSet()

  for ((rowNum, line) in dataLines) {
    val parsed = splitCsvLine(line)
    if (parsed == null) {
      errors += "Row $rowNum: unterminated quote — multi-line values aren't supported"
      continue
    }
    val cells = parsed.map { it.trim() }
    if (cells.size != 3) {
      errors += "Row $rowNum: expected 3 columns (team name, owner name, owner email), found ${cells.size}"
      continue
    }
    val (teamName, ownerName, ownerEmailRaw) = cells
    val ownerEmail = ownerEmailRaw.lowercase()

    when {
      teamName.isBlank() -> errors += "Row $rowNum: team name is blank"
      ownerName.isBlank() -> errors += "Row $rowNum: owner name is blank"
      ownerEmailRaw.isBlank() -> errors += "Row $rowNum: owner email is blank"
      !EMAIL_REGEX.matches(ownerEmailRaw) -> errors += "Row $rowNum: \"$ownerEmailRaw\" is not a valid email"
      ownerEmail in seenEmails -> errors += "Row $rowNum: duplicate owner email \"$ownerEmail\""
      else -> {
        seenEmails += ownerEmail
        rows += ParsedTeamRow(teamName, ownerName, ownerEmail)
      }
    }
  }

  return CsvParseResult(if (errors.isEmpty()) rows else emptyList(), errors)
}

/**
 * Split a single CSV line into fields. Double-quoted fields may contain commas (so a team name can be
 * `"Sharks, Inc."`), with `""` as an escaped quote. Returns `null` if the line ends inside an open
 * quote — i.e. a value that would continue onto the next line; multi-line fields are deliberately
 * unsupported and the caller surfaces this as a row error. Trimming is the caller's job.
 */
private fun splitCsvLine(line: String): List<String>? {
  val fields = mutableListOf<String>()
  val current = StringBuilder()
  var inQuotes = false
  var i = 0
  while (i < line.length) {
    val c = line[i]
    when {
      inQuotes && c == '"' && i + 1 < line.length && line[i + 1] == '"' -> {
        current.append('"'); i++ // escaped quote
      }
      c == '"' -> inQuotes = !inQuotes
      c == ',' && !inQuotes -> { fields += current.toString(); current.clear() }
      else -> current.append(c)
    }
    i++
  }
  if (inQuotes) return null // unterminated quoted field — a multi-line value we don't accept
  fields += current.toString()
  return fields
}

// ── Upload modal ─────────────────────────────────────────────────────────────

/**
 * Drag-drop / file-picker modal for bulk-adding teams from a CSV during draft setup. Parses and
 * validates entirely client-side (see [parseTeamsCsv]); on success it hands the rows to [onImport]
 * (which appends them to the wizard's in-memory roster) and shows a confirmation.
 */
@Composable
fun TeamCsvUploadModal(
  existingEmails: Set<String>,
  onImport: (List<ParsedTeamRow>) -> Int,
  onDismiss: () -> Unit,
) {
  var fileName by remember { mutableStateOf<String?>(null) }
  var fileText by remember { mutableStateOf<String?>(null) }
  var errors by remember { mutableStateOf<List<String>?>(null) }
  var importedCount by remember { mutableStateOf<Int?>(null) }
  var dragActive by remember { mutableStateOf(false) }
  // Hidden native file input, clicked programmatically from the drop zone.
  val fileInput = remember { arrayOfNulls<HTMLInputElement>(1) }

  fun readFile(file: File) {
    errors = null
    val reader = FileReader()
    reader.onload = {
      fileText = reader.result as? String ?: ""
      fileName = file.name
    }
    reader.readAsText(file)
  }

  PanelDialog(labelledBy = "csv-upload-title", onDismiss = onDismiss) {
    val count = importedCount
    if (count != null) {
      SuccessView(count = count, onDone = onDismiss)
    } else {
      PickView(
        fileName = fileName,
        errors = errors,
        dragActive = dragActive,
        onBrowse = { fileInput[0]?.click() },
        onDragChange = { dragActive = it },
        onDropFile = { readFile(it) },
        onCancel = onDismiss,
        onSubmit = {
          val text = fileText ?: return@PickView
          val result = parseTeamsCsv(text, existingEmails)
          if (result.errors.isNotEmpty()) {
            errors = result.errors
          } else {
            importedCount = onImport(result.rows)
          }
        },
      )
      // Hidden input — kept mounted so its ref persists. A native "change" listener reads the
      // chosen file (sidesteps Compose's typed change-event handling for file inputs).
      DomInput(
        type = InputType.File,
        attrs = {
          attr("accept", ".csv,text/csv")
          style { property("display", "none") }
          ref { el ->
            val input = el as HTMLInputElement
            fileInput[0] = input
            val listener: (Event) -> Unit = { input.files?.get(0)?.let { readFile(it) } }
            input.addEventListener("change", listener)
            onDispose {
              input.removeEventListener("change", listener)
              fileInput[0] = null
            }
          }
        },
      )
    }
  }
}

@Composable
private fun PickView(
  fileName: String?,
  errors: List<String>?,
  dragActive: Boolean,
  onBrowse: () -> Unit,
  onDragChange: (Boolean) -> Unit,
  onDropFile: (File) -> Unit,
  onCancel: () -> Unit,
  onSubmit: () -> Unit,
) {
  Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px).toAttrs()) {
    Icon("upload-simple", IconWeight.Bold, Modifier.fontSize(26.px).color(DmColor.brass400))
    Div(attrs = Modifier.toAttrs { id("csv-upload-title") }) {
      SpanText("Upload teams", Modifier.roleH2().displayBlock())
    }
  }

  // Drag-drop / click zone.
  Div(
    attrs = Modifier.fillMaxWidth().displayFlex().flexDirection(FlexDirection.Column)
      .alignItems(AlignItems.Center).justifyContent(JustifyContent.Center).gap(6.px)
      .padding(topBottom = 22.px, leftRight = 16.px).cursor(Cursor.Pointer)
      .border(1.px, if (dragActive) LineStyle.Solid else LineStyle.Dashed, if (dragActive) DmColor.brass400 else DmColor.border)
      .borderRadius(DmEffects.radiusSm).backgroundColor(if (dragActive) DmColor.surfacePanel else DmColor.surfaceInset)
      .toAttrs {
        attr("role", "button")
        attr("aria-label", "Choose a CSV file")
        onClick { onBrowse() }
        onDragOver { it.preventDefault(); onDragChange(true) }
        onDragEnter { it.preventDefault(); onDragChange(true) }
        onDragLeave { onDragChange(false) }
        onDrop { ev ->
          ev.preventDefault()
          onDragChange(false)
          ev.dataTransfer?.files?.get(0)?.let { onDropFile(it) }
        }
      },
  ) {
    Icon("file-csv", IconWeight.Regular, Modifier.fontSize(30.px).color(DmColor.textMuted))
    SpanText(
      fileName ?: "Drag a CSV here, or tap to browse",
      Modifier.fontFamily(*DmType.body).fontSize(DmType.textSm)
        .color(if (fileName != null) DmColor.text else DmColor.textMuted).displayBlock(),
    )
  }

  // Format overview.
  Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(4.px).toAttrs()) {
    SpanText("Required format", Modifier.dmStamp().color(DmColor.glass400).displayBlock())
    SpanText(
      "A header row plus one team per line — 3 columns:",
      Modifier.fontFamily(*DmType.body).fontSize(DmType.textXs).color(DmColor.textMuted).lineHeight(1.5).displayBlock(),
    )
    Div(
      attrs = Modifier.fillMaxWidth().padding(10.px).backgroundColor(DmColor.surfaceInset)
        .border(1.px, LineStyle.Solid, DmColor.border).borderRadius(DmEffects.radiusSm)
        .styleModifier { property("overflow-x", "auto") }.toAttrs(),
    ) {
      SpanText(
        "Team Name,Owner Name,Owner Email\nBoiler Room Bandits,Avery Lin,avery.lin@gmail.com",
        Modifier.fontFamily(*DmType.mono).fontSize(DmType.textXs).color(DmColor.textSecondary)
          .whiteSpace(WhiteSpace.Pre).displayBlock(),
      )
    }
  }

  if (errors != null) {
    Toast(title = "Couldn't import — fix and re-upload", tone = ToastTone.Rust)
    // Each error on its own line (the Toast body collapses newlines); scroll if the list is long.
    Div(
      attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(3.px)
        .maxHeight(140.px).styleModifier { property("overflow-y", "auto") }.toAttrs(),
    ) {
      errors.forEach { err ->
        SpanText(
          err,
          Modifier.fontFamily(*DmType.mono).fontSize(DmType.textXs).lineHeight(1.5)
            .color(DmColor.rust400).displayBlock(),
        )
      }
    }
  }

  Div(attrs = Modifier.displayFlex().gap(10.px).toAttrs()) {
    Button("Cancel", variant = ButtonVariant.Ghost, onClick = onCancel, modifier = Modifier.flex(1))
    Button(
      "Submit", variant = ButtonVariant.Primary, icon = "check",
      disabled = fileName == null, onClick = onSubmit, modifier = Modifier.flex(1),
    )
  }
}

@Composable
private fun SuccessView(count: Int, onDone: () -> Unit) {
  Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(10.px).toAttrs()) {
    Icon("check-circle", IconWeight.Fill, Modifier.fontSize(28.px).color(DmColor.success))
    Div(attrs = Modifier.toAttrs { id("csv-upload-title") }) {
      SpanText("Added $count team${if (count == 1) "" else "s"}", Modifier.roleH2().displayBlock())
    }
  }
  SpanText(
    "They're on the roster below. You can keep adding teams or move on.",
    Modifier.fontFamily(*DmType.body).fontSize(DmType.textSm).color(DmColor.textSecondary).lineHeight(1.55),
  )
  Button("Done", variant = ButtonVariant.Primary, fullWidth = true, onClick = onDone)
}
